import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useDeletePayout } from "@entities/campaigns/lib/hooks";
import { useCreateManualGstPayout } from "@entities/campaigns/lib/hooks/useCreateManualGstPayout";
import { useCreateManualPayout } from "@entities/campaigns/lib/hooks/useCreateManualPayout";
import { useEditManualGstPayout } from "@entities/campaigns/lib/hooks/useEditManualGstPayout";
import { useEditManualPayout } from "@entities/campaigns/lib/hooks/useEditManualPayout";
import { ToastService } from "@shared/lib/services";
import {
  ICreateManualGstPayout,
  ICreateManualPayout,
  IInvoice,
  IPayment,
} from "@shared/lib/types/briefCampains";
import { Button } from "@shared/ui/Button";
import { Col, Flex, Modal, Row } from "antd";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { PaymentEntry } from "./PaymentEntry";
import { PaymentExternalStatus } from "./PaymentExternalStatus";
import { PaymentStatus } from "./PayoutStatus";
import { PayoutVerificationLink } from "./PayoutVerificationLink";
import styles from "./styles.module.scss";

export function Payouts({
  payments,
  campaignInfluencerId,
  refetch,
  isRefetching,
  invoices,
}: {
  payments: IPayment[];
  campaignInfluencerId: string;
  refetch: () => void;
  isRefetching: boolean;
  invoices: IInvoice[];
}) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<IPayment | null>(null);

  const handleEditClick = (payment: IPayment) => {
    setSelectedPayment(payment);
    setIsEditModalOpen(true);
  };
  const { createManualPayout, isCreating, isCreated } =
    useCreateManualPayout(campaignInfluencerId);
  const { editManualPayout, isEditing, isEdited } =
    useEditManualPayout(campaignInfluencerId);
  const { deletePayout, isDeleting, isDeleted, variables } =
    useDeletePayout(campaignInfluencerId);

  const {
    createManualGstPayout,
    isCreating: isCreatingManualGstPayout,
    isCreated: isCreatedManualGstPayout,
  } = useCreateManualGstPayout(campaignInfluencerId);
  const {
    editManualGstPayout,
    isEditing: isEditingManualGstPayout,
    isEdited: isEditedManualGstPayout,
  } = useEditManualGstPayout(campaignInfluencerId);

  useEffect(() => {
    if (
      isCreated ||
      isEdited ||
      isCreatedManualGstPayout ||
      isEditedManualGstPayout
    ) {
      ToastService.success("Payment created successfully");
      setIsEditModalOpen(false);
      setSelectedPayment(null);
    }
  }, [isCreated, isEdited, isCreatedManualGstPayout, isEditedManualGstPayout]);

  useEffect(() => {
    if (isDeleted) {
      ToastService.success("Payment deleted successfully");
    }
  }, [isDeleted]);

  const handleSubmit = (
    values: ICreateManualPayout | ICreateManualGstPayout,
    isGst: boolean
  ) => {
    if (selectedPayment) {
      if (isGst) {
        editManualGstPayout({
          ...values,
          vendorPaymentId: selectedPayment._id,
          campaignInfluencerId,
        });
      } else {
        editManualPayout({
          ...values,
          vendorPaymentId: selectedPayment._id,
          campaignInfluencerId,
        });
      }
    } else {
      if (isGst) {
        createManualGstPayout({ ...values, campaignInfluencerId });
      } else {
        createManualPayout({ ...values, campaignInfluencerId });
      }
    }
  };

  return (
    <>
      <div className={styles.payoutCard}>
        <div className={styles.payoutHeader}>
          <Flex align="center">
            Payouts
            <SyncOutlined
              style={{ marginLeft: 12, fontSize: 14 }}
              onClick={() => refetch()}
              spin={isRefetching}
            />
          </Flex>
          <button
            className={styles.payoutAddBtn}
            onClick={() => {
              setSelectedPayment(null);
              setIsEditModalOpen(true);
            }}
          >
            <PlusOutlined style={{ marginRight: 4 }} /> Add Payment
          </button>
        </div>
        <div className={styles.payoutList}>
          {payments.map((entry) => {
            // Title and icons
            let isManual =
              entry.subType === "Manual" || entry.subType === "Manual-Gst";
            let link = entry?.payout?.payoutLink;
            let channel = entry?.isAuto ? "Razorpay" : null;

            // Amount logic
            const amountClass = styles.payoutAmount;
            const amountSign = entry?.type === "Debit" ? "+" : "-";

            // Date and user (fallback to blank)
            const dateStr = entry?.createdAt
              ? format(new Date(entry?.createdAt), "dd MMM yyyy")
              : "";
            const userStr = entry?.createdBy
              ? `by ${entry?.createdBy?.firstName} ${entry?.createdBy?.lastName}`
              : "by Admin ";

            return (
              <Row key={entry._id}>
                <Col span={8}>
                  <div className={styles.payoutLeft} style={{ width: "100%" }}>
                    <div className={styles.payoutTitle}>
                      {link ? (
                        <>
                          <Button
                            type="link"
                            style={{ padding: 0 }}
                            onClick={() => {
                              window.open(link, "_blank");
                            }}
                          >
                            Payout Link
                          </Button>
                          <CopyOutlined
                            style={{
                              marginLeft: 2,
                              cursor: "pointer",
                              fontSize: 12,
                            }}
                            onClick={() => {
                              if (link) {
                                navigator.clipboard.writeText(link);
                                ToastService.success(
                                  "Link copied to clipboard"
                                );
                              }
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <span>
                            {entry.subType} {entry.remarks}
                          </span>
                          {isManual && !entry.isBankVerificationRequired && (
                            <EditOutlined
                              className={styles.payoutEditIcon}
                              onClick={() => handleEditClick(entry)}
                            />
                          )}
                        </>
                      )}
                    </div>
                    {entry.linkedInvoice?._id ? (
                      <div className={styles.payoutInvoice}>
                        Linked Invoice: {entry.linkedInvoice?.remarks}
                      </div>
                    ) : null}
                    <PayoutVerificationLink record={entry} />
                    <div className={styles.payoutDate}>
                      {dateStr} {userStr}
                    </div>
                  </div>
                </Col>
                <Col span={8} style={{ textAlign: "center" }}>
                  {entry.isBankVerificationRequired ? (
                    <PaymentExternalStatus record={entry} />
                  ) : channel ? (
                    <Flex vertical justify="center" align="center" gap={5} style={{width: "100%"}}>
                      <span className={styles.payoutChannel}>{channel}</span>
                      <PaymentStatus record={entry}/>
                    </Flex>
                  ) : null}
                </Col>
                <Col span={8}>
                  <Flex justify="end" gap={8}>
                    <div style={{ textAlign: "right" }}>
                      <div className={amountClass}>
                        {amountSign}₹{entry.amount}
                      </div>
                    </div>
                    {isManual && !entry.isBankVerificationRequired && (
                      <Button
                        danger
                        loading={
                          isDeleting && variables?.vendorPaymentId === entry._id
                        }
                        onClick={() =>
                          deletePayout({ vendorPaymentId: entry._id })
                        }
                        icon={<DeleteOutlined />}
                      />
                    )}
                  </Flex>
                </Col>
              </Row>
            );
          })}
        </div>
      </div>
      <Modal
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={null}
        destroyOnClose
        title={selectedPayment ? "Edit Payment" : "Add Payment"}
      >
        <PaymentEntry
          invoices={invoices}
          initialValues={
            selectedPayment
              ? {
                  ...selectedPayment,
                }
              : undefined
          }
          onSubmit={handleSubmit}
          isSubmitting={
            isCreating ||
            isEditing ||
            isCreatingManualGstPayout ||
            isEditingManualGstPayout
          }
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedPayment(null);
          }}
        />
      </Modal>
    </>
  );
}
